import { MarketCategory } from '@vertex-protocol/react-client';
import { joinClassNames } from '@vertex-protocol/web-common';
import { SearchBar, Z_INDEX } from '@vertex-protocol/web-ui';
import { MarketCategoryFilter } from 'client/components/MarketCategoryFilter/MarketCategoryFilter';
import { useAnalyticsContext } from 'client/modules/analytics/AnalyticsContext';
import { TradingMarketSwitcherPopoverTrigger } from 'client/modules/trading/components/TradingMarketSwitcher/TradingMarketSwitcherPopoverTrigger';
import { TradingMarketSwitcherTable } from 'client/modules/trading/components/TradingMarketSwitcher/TradingMarketSwitcherTable';
import { useTradingMarketSwitcher } from 'client/modules/trading/hooks/useTradingMarketSwitcher';
import { MarketSwitcherProps } from 'client/modules/trading/layout/types';
import { useEffect, useRef } from 'react';

interface TradingMarketSwitcherProps extends MarketSwitcherProps {
  productId: number | undefined;
  defaultMarketCategory: MarketCategory;
}

export function TradingMarketSwitcher({
  productId,
  defaultMarketCategory,
  triggerClassName,
}: TradingMarketSwitcherProps) {
  const { trackEvent } = useAnalyticsContext();

  const {
    selectedMarket,
    displayedMarkets,
    isLoading,
    disableMarketSwitcherButton,
    selectedMarketCategory,
    setSelectedMarketCategory,
    toggleIsFavoritedMarket,
    isMarketSwitcherOpen,
    setIsMarketSwitcherOpen,
    query,
    setQuery,
    disableFavoriteButton,
  } = useTradingMarketSwitcher(productId, defaultMarketCategory);

  // Close dropdown when clicking outside
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMarketSwitcherOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsMarketSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMarketSwitcherOpen, setIsMarketSwitcherOpen]);

  return (
    <div ref={containerRef} className="relative">
      <TradingMarketSwitcherPopoverTrigger
        disabled={disableMarketSwitcherButton}
        open={isMarketSwitcherOpen}
        selectedMarket={selectedMarket}
        className={triggerClassName}
        onClick={() => setIsMarketSwitcherOpen(!isMarketSwitcherOpen)}
      />
      {isMarketSwitcherOpen && (
        <div
          className={joinClassNames(
            `absolute left-0 top-full ${Z_INDEX.popover}`,
            'bg-surface-card border-stroke flex flex-col gap-y-1 rounded-b-lg border p-1',
            'shadow-elevation-strong',
            'min-h-[300px] max-h-[80vh] w-full',
          )}
        >
          <div className="flex flex-col gap-y-2 px-2">
            <SearchBar
              sizeVariant="xs"
              placeholder="Search"
              query={query}
              setQuery={setQuery}
            />
            <MarketCategoryFilter
              marketCategory={selectedMarketCategory}
              setMarketCategory={setSelectedMarketCategory}
            />
          </div>
          <TradingMarketSwitcherTable
            key={selectedMarketCategory}
            disableFavoriteButton={disableFavoriteButton}
            toggleIsFavoritedMarket={toggleIsFavoritedMarket}
            markets={displayedMarkets}
            isLoading={isLoading}
            onRowClick={() => {
              trackEvent({
                type: 'market_entrypoint_clicked',
                data: {
                  entrypoint: 'trade_dropdown',
                },
              });
              setIsMarketSwitcherOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
